import type { ReactNode } from "react";
import clsx from "clsx";
import { ToneMarker } from "../status/StatusBadge";
import type { Tone } from "../status/statusMap";

export type BannerTone = Tone | "good";

/**
 * Inline notice (§3): cause + next step in at most two lines, and one link. Marker + title carry the tone.
 * `link` (new) or `action` (legacy) render the trailing action; `body` or `children` the explanation.
 */
export function Banner({ tone = "info", title, body, children, link, action, className }: { tone?: BannerTone; title?: ReactNode; body?: ReactNode; children?: ReactNode; link?: ReactNode; action?: ReactNode; className?: string }) {
  const t: Tone = tone === "good" ? "ok" : tone;
  const color = { ok: "text-ok", info: "text-info", warn: "text-warn", bad: "text-bad", neutral: "text-neutral" }[t];
  const bg = { ok: "bg-ok-soft", info: "bg-info-soft", warn: "bg-warn-soft", bad: "bg-bad-soft", neutral: "bg-neutral-soft" }[t];
  return (
    <div className={clsx("flex items-start gap-3 rounded-card px-4 py-3.5 text-fg", bg, className)} role={t === "bad" || t === "warn" ? "alert" : "status"}>
      <span className={clsx("mt-[5px] inline-flex shrink-0", color)}><ToneMarker tone={t} /></span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {title && <div className="font-semibold">{title}</div>}
        {(body ?? children) && <div className="text-small text-fg-2">{body ?? children}</div>}
        {link && <div className="text-small font-semibold">{link}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
